import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import QrScanner from 'qr-scanner'
import { useMobileAuth } from '../contexts/MobileAuthContext'
import { validateQrLogin } from '../utils/validateQrLogin'
import { classifyApiError } from '../utils/api'
import ErrorState from '../components/ErrorState'
import logo from '../assets/logo.png'

const APP_VERSION = '1.0.0'
const MAX_UPLOAD_SIZE = 8 * 1024 * 1024 // 8MB

// idle -> starting -> scanning -> validating -> (dashboard | error)
const QRLoginPage = () => {
  const navigate = useNavigate()
  const { login } = useMobileAuth()
  const videoRef = useRef(null)
  const scannerRef = useRef(null)
  const fileInputRef = useRef(null)
  const [phase, setPhase] = useState('idle')
  const [cameraStatus, setCameraStatus] = useState('starting') // starting | ready | denied | unsupported
  const [error, setError] = useState(null) // { type, message }

  const stopScanner = () => {
    scannerRef.current?.stop()
    scannerRef.current?.destroy()
    scannerRef.current = null
  }

  const handleDecoded = async (raw) => {
    stopScanner()
    setPhase('validating')

    let payload
    try {
      payload = JSON.parse(raw)
    } catch {
      setError({ type: 'malformed' })
      setPhase('error')
      return
    }

    try {
      const { companyName } = await validateQrLogin(payload)
      login({ ...payload, companyName })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (err.type) {
        setError({ type: err.type, message: err.message })
      } else {
        setError({ type: classifyApiError(err) })
      }
      setPhase('error')
    }
  }

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file on retry
    if (!file) return

    if (!file.type.startsWith('image/') || file.size > MAX_UPLOAD_SIZE) {
      setError({ type: 'invalid_image' })
      setPhase('error')
      return
    }

    setPhase('validating')

    let result
    try {
      result = await QrScanner.scanImage(file, { returnDetailedScanResult: true })
    } catch {
      setError({ type: 'no_qr_in_image' })
      setPhase('error')
      return
    }

    await handleDecoded(result.data)
  }

  const openFilePicker = () => fileInputRef.current?.click()

  useEffect(() => {
    if (phase !== 'scanning') return

    let cancelled = false
    setCameraStatus('starting')

    QrScanner.hasCamera().then((hasCamera) => {
      if (cancelled) return
      if (!hasCamera) {
        setCameraStatus('unsupported')
        return
      }

      const scanner = new QrScanner(
        videoRef.current,
        (result) => handleDecoded(result.data),
        {
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
        }
      )
      scannerRef.current = scanner

      scanner
        .start()
        .then(() => !cancelled && setCameraStatus('ready'))
        .catch(() => !cancelled && setCameraStatus('denied'))
    })

    return () => {
      cancelled = true
      stopScanner()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const startScan = () => setPhase('scanning')
  const retry = () => {
    setError(null)
    setPhase('idle')
  }

  return (
    <div className="flex flex-col h-full bg-gray-950 safe-top safe-bottom px-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4">
        <div className="logo-spin-border inline-flex items-center justify-center shrink-0">
          <img src={logo} alt="SF" className="w-12 h-12 object-contain rounded-full" />
        </div>
        <div>
          <h1 className="text-xl leading-none" style={{ fontFamily: 'Blare, sans-serif' }}>
            <span className="text-white">Solid</span><span style={{ color: '#25C6A4' }}>.</span><span className="text-white">Flow</span>
          </h1>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-gray-300 bg-white/10 mt-1.5"
            style={{ fontFamily: "'Stolzl', sans-serif" }}
          >
            <span className="material-icons" style={{ fontSize: '11px', color: '#25C6A4' }}>hub</span>
            Hub de Integrações
          </span>
        </div>
      </div>

      {/* Main area */}
      <div className="relative flex-1 rounded-3xl overflow-hidden bg-black border border-white/10 flex items-center justify-center">
        {phase === 'idle' && (
          <div className="flex flex-col items-center gap-5 px-8 text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(37,198,164,0.15)' }}
            >
              <span className="material-icons" style={{ fontSize: '36px', color: '#25C6A4' }}>qr_code_scanner</span>
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Entrar com QR Code</p>
              <p className="text-gray-400 text-xs mt-1">Escaneie o código gerado para sua empresa</p>
            </div>
            <button
              type="button"
              onClick={startScan}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white active:scale-95 transition"
              style={{ backgroundColor: '#25C6A4' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>qr_code_2</span>
              Escanear QR Code
            </button>
            <p className="text-gray-500 text-[11px] max-w-[220px] -mt-1">
              Não conseguiu escanear? Se tiver o QR Code salvo, envie a imagem clicando no botão abaixo
            </p>
            <button
              type="button"
              onClick={openFilePicker}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-gray-300 bg-white/10 active:scale-95 transition"
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>upload_file</span>
              Upload do QR Code
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileSelected}
            />
          </div>
        )}

        {phase === 'scanning' && (
          <>
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />

            {cameraStatus === 'starting' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
                <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full" />
                <p className="text-xs text-gray-300">Iniciando câmera…</p>
              </div>
            )}

            {cameraStatus === 'denied' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 px-6 text-center">
                <span className="material-icons text-gray-400" style={{ fontSize: '36px' }}>videocam_off</span>
                <p className="text-sm text-white font-medium">Acesso à câmera negado</p>
                <p className="text-xs text-gray-400">Permita o uso da câmera nas configurações do navegador para escanear o QR Code.</p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-2 px-5 py-2 rounded-full text-xs font-semibold text-white bg-white/10 active:scale-95 transition"
                >
                  Voltar
                </button>
              </div>
            )}

            {cameraStatus === 'unsupported' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 px-6 text-center">
                <span className="material-icons text-gray-400" style={{ fontSize: '36px' }}>no_photography</span>
                <p className="text-sm text-white font-medium">Nenhuma câmera encontrada</p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-2 px-5 py-2 rounded-full text-xs font-semibold text-white bg-white/10 active:scale-95 transition"
                >
                  Voltar
                </button>
              </div>
            )}

            {cameraStatus === 'ready' && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <span className="px-3 py-1.5 rounded-full bg-black/60 text-xs text-white flex items-center gap-1.5">
                  <span className="material-icons" style={{ fontSize: '14px' }}>qr_code_scanner</span>
                  Aponte para o QR Code
                </span>
              </div>
            )}
          </>
        )}

        {phase === 'validating' && (
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full" />
            <p className="text-xs text-gray-300">Validando acesso…</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="w-full">
            <ErrorState type={error?.type} message={error?.message} onRetry={retry} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-3 flex items-center justify-center gap-1.5 text-gray-500">
        <span className="material-icons" style={{ fontSize: '13px' }}>smartphone</span>
        <span className="text-[11px] font-mono tracking-wide">v{APP_VERSION}</span>
      </div>
    </div>
  )
}

export default QRLoginPage
